import { useMemo, useState } from 'react'

import { Loading, mainColors, MBold, SearchInput, SNormal } from '@avantstay/backoffice-core'
import { IconSearch } from '@avantstay/backoffice-vectors'

import { HotelOption } from '../../AsyncHotelFilter.types'
import * as S from './HotelsList.styles'

interface HotelsListProps {
  options: HotelOption[]
  initialValue?: string
  isLoadingOptions?: boolean
  onChange: (value: string) => void
}

export function HotelsList({ options, initialValue, isLoadingOptions, onChange }: HotelsListProps) {
  const [selectedOptionId, setSelectedOptionId] = useState(initialValue)
  const [searchFilterValue, setSearchFilterValue] = useState<string | undefined>('')

  const handleOnChange = (value: string) => {
    setSelectedOptionId(value)
    onChange(value)
  }

  const optionsMatchingSearch = useMemo(() => {
    if (!options.length || !searchFilterValue) {
      return options
    }

    const normalizedSearch = searchFilterValue.toLowerCase()

    return options.filter(option => {
      const labelString = option.label.toLowerCase()
      return labelString.includes(normalizedSearch)
    })
  }, [options, searchFilterValue])

  const hasOptionsMatchingSearch = optionsMatchingSearch.length > 0

  if (isLoadingOptions) return <Loading size="4px" />

  return (
    <>
      <SearchInput
        placeholder="Search for a hotel"
        inputLeftIcon={<IconSearch width={16} height={16} />}
        onChange={setSearchFilterValue}
      />

      {hasOptionsMatchingSearch ? (
        <S.OptionsList>
          {optionsMatchingSearch.map(({ image, label, unitCount, value }) => (
            <S.OptionsListItem
              key={value}
              $isSelected={selectedOptionId === value}
              onClick={() => handleOnChange(value)}
            >
              {image ? <S.HotelImg src={image} /> : <S.HotelFallbackImg />}
              <S.HotelDetails>
                <MBold>{label}</MBold>
                <SNormal color={mainColors.neutralExtremelyDark}>Unit Count: {unitCount}</SNormal>
              </S.HotelDetails>
            </S.OptionsListItem>
          ))}
        </S.OptionsList>
      ) : (
        <S.EmptyResults>No results found</S.EmptyResults>
      )}
    </>
  )
}
